import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookFact, BookFactStatus } from '../../types';
import { getGraphQLClient, SAVE_BOOK_FACT, DELETE_BOOK_FACT } from '../../lib/graphql';
import { captureException } from '../../lib/sentry';
import { AutoResizeTextarea, Button, Card } from '../common';

interface FactsPanelProps {
  bookId: string;
  facts: BookFact[];
}

const STATUSES: BookFactStatus[] = ['verified', 'assumed', 'disputed'];

// Human-curated, book-level trusted facts the AI must stay consistent with —
// the trust anchor for fact-checking. Verified facts ground generation as fact;
// assumed/disputed ones are fed with a qualifier so the AI hedges. Repo-
// authoritative: they live in _facts.md and sync both ways.
export default function FactsPanel({ bookId, facts: initial }: FactsPanelProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(true);
  const [facts, setFacts] = useState<BookFact[]>(initial);
  const [adding, setAdding] = useState(false);

  const save = async (vars: Record<string, unknown>) => {
    const res = await getGraphQLClient().request<{
      saveBookFact: { bookFact: BookFact | null; errors: string[] };
    }>(SAVE_BOOK_FACT, { bookId, ...vars });
    return res.saveBookFact?.bookFact ?? null;
  };

  const upsert = (f: BookFact) =>
    setFacts((prev) => {
      const idx = prev.findIndex((x) => x.id === f.id);
      if (idx === -1) return [...prev, f].sort((a, b) => a.position - b.position);
      const next = [...prev];
      next[idx] = f;
      return next;
    });

  const onAdd = async (claim: string, source: string, status: BookFactStatus) => {
    try {
      const saved = await save({ claim, source, status });
      if (saved) upsert(saved);
      setAdding(false);
    } catch (err) {
      captureException(err);
    }
  };

  const onPatch = async (fact: BookFact, patch: Partial<BookFact>) => {
    upsert({ ...fact, ...patch }); // optimistic
    try {
      const saved = await save({ bookFactId: fact.id, ...patch });
      if (saved) upsert(saved);
    } catch (err) {
      captureException(err);
      upsert(fact); // revert
    }
  };

  const onDelete = async (fact: BookFact) => {
    const prev = facts;
    setFacts((f) => f.filter((x) => x.id !== fact.id));
    try {
      await getGraphQLClient().request(DELETE_BOOK_FACT, { bookFactId: fact.id });
    } catch (err) {
      captureException(err);
      setFacts(prev); // revert
    }
  };

  const activeCount = facts.filter((f) => f.includeInGeneration).length;

  return (
    <Card padding="md" className="mb-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-800"
        >
          {collapsed ? '▸' : '▾'}
        </button>
        <span className="font-semibold text-gray-800 dark:text-gray-100">
          🔎 {t('bookFacts.title', 'Facts')}
        </span>
        <span className="text-xs text-gray-400">
          {facts.length === 0
            ? ''
            : t('bookFacts.summary', {
                active: activeCount,
                total: facts.length,
                defaultValue: '{{active}} of {{total}} grounding the AI',
              })}
        </span>
      </div>

      {!collapsed && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-gray-400 leading-relaxed">
            {t(
              'bookFacts.subtitle',
              'Trusted facts the AI must stay consistent with — dates, names, figures, sources. Edit them here or in your repo (_facts.md); changes sync both ways.'
            )}
          </p>

          {facts.length === 0 && !adding && (
            <p className="text-xs text-gray-400">
              {t('bookFacts.empty', 'No facts yet. Add one to ground the AI and catch invented claims.')}
            </p>
          )}

          <ul className="space-y-1.5">
            {facts.map((fact) => (
              <FactRow
                key={fact.id}
                fact={fact}
                onPatch={(patch) => onPatch(fact, patch)}
                onDelete={() => onDelete(fact)}
              />
            ))}
          </ul>

          {adding ? (
            <AddFactForm onCancel={() => setAdding(false)} onAdd={onAdd} />
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
              {t('bookFacts.add', '+ Add fact')}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

function FactRow({
  fact,
  onPatch,
  onDelete,
}: {
  fact: BookFact;
  onPatch: (patch: Partial<BookFact>) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [claim, setClaim] = useState(fact.claim);
  const [source, setSource] = useState(fact.source);

  const dirty = claim !== fact.claim || source !== fact.source;

  return (
    <li className="border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="flex items-center gap-2 px-2 py-1.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex-1 text-left text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-indigo-600 truncate"
        >
          {open ? '▾ ' : '▸ '}
          {fact.claim?.trim() || t('bookFacts.untitled', '(empty fact)')}
        </button>

        {/* Status — how settled the claim is */}
        <select
          value={fact.status}
          onChange={(e) => onPatch({ status: e.target.value as BookFactStatus })}
          className="text-xs bg-transparent border border-gray-200 dark:border-gray-700 rounded px-1 py-0.5 text-gray-500"
          title={t('bookFacts.statusHint', 'How settled this claim is')}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`bookFacts.status.${s}`, s)}
            </option>
          ))}
        </select>

        {/* Use-in-generation toggle */}
        <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={fact.includeInGeneration}
            onChange={(e) => onPatch({ includeInGeneration: e.target.checked })}
          />
          {t('bookFacts.useInGeneration', 'Use')}
        </label>

        <button
          type="button"
          onClick={onDelete}
          aria-label={t('common.delete', 'Delete')}
          className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {open && (
        <div className="px-2 pb-2 space-y-2">
          <AutoResizeTextarea
            value={claim}
            onChange={(e) => setClaim(e.target.value)}
            minRows={2}
            maxRows={10}
            placeholder={t('bookFacts.claimPlaceholder', 'The claim, stated plainly…')}
            className="w-full px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800"
          />
          <input
            type="text"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder={t('bookFacts.sourcePlaceholder', 'Source (book, URL, research…)')}
            className="w-full text-sm bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-indigo-400 focus:outline-none py-1"
          />
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={!dirty || !claim.trim()}
              onClick={() => onPatch({ claim, source })}
            >
              {t('common.save', 'Save')}
            </Button>
            {dirty && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setClaim(fact.claim);
                  setSource(fact.source);
                }}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            )}
            <span className="text-[10px] text-gray-300 dark:text-gray-600 ml-auto">_facts.md</span>
          </div>
        </div>
      )}
    </li>
  );
}

function AddFactForm({
  onAdd,
  onCancel,
}: {
  onAdd: (claim: string, source: string, status: BookFactStatus) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [claim, setClaim] = useState('');
  const [source, setSource] = useState('');
  const [status, setStatus] = useState<BookFactStatus>('verified');

  return (
    <div className="border border-indigo-200 dark:border-indigo-800 rounded-lg p-2 space-y-2">
      <AutoResizeTextarea
        value={claim}
        onChange={(e) => setClaim(e.target.value)}
        minRows={2}
        maxRows={8}
        placeholder={t('bookFacts.claimPlaceholder', 'The claim, stated plainly…')}
        className="w-full px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800"
      />
      <input
        type="text"
        value={source}
        onChange={(e) => setSource(e.target.value)}
        placeholder={t('bookFacts.sourcePlaceholder', 'Source (book, URL, research…)')}
        className="w-full text-sm bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-indigo-400 focus:outline-none py-1"
      />
      <div className="flex items-center gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as BookFactStatus)}
          className="text-xs bg-transparent border border-gray-200 dark:border-gray-700 rounded px-1 py-0.5 text-gray-500"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`bookFacts.status.${s}`, s)}
            </option>
          ))}
        </select>
        <Button
          variant="primary"
          size="sm"
          disabled={!claim.trim()}
          onClick={() => onAdd(claim.trim(), source.trim(), status)}
        >
          {t('common.add', 'Add')}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          {t('common.cancel', 'Cancel')}
        </Button>
      </div>
    </div>
  );
}
